import * as THREE from 'three';
import { Vector2, Vector3 } from '../types';

/**
 * Utility class for mapping UV coordinates to 3D world positions
 * Uses vertex-based interpolation for accurate texture-to-geometry mapping
 */
export class UVSurfaceMapper {
    /**
     * Maps UV coordinates (0-1 range) to 3D position on mesh surface
     * @param geometry - THREE.BufferGeometry to sample from
     * @param uv - UV coordinates (0-1 range)
     * @returns Local 3D position on the mesh surface
     */
    static getPositionFromUV(geometry: THREE.BufferGeometry, uv: Vector2): Vector3 {
        if (!geometry.attributes.position) {
            throw new Error('Geometry has no position attribute');
        }
        
        const positions = geometry.attributes.position as THREE.BufferAttribute;
        const uvs = geometry.attributes.uv as THREE.BufferAttribute;
        const indices = geometry.index;
        
        if (!uvs) {
            // No UV coordinates available, fall back to mathematical center
            console.warn('Geometry has no UV coordinates, using center position');
            return new Vector3(0, 0, 0);
        }
        
        // Find the triangle containing this UV point
        const triangleData = this.findTriangleContainingUV(uv, uvs, indices);
        
        if (!triangleData) {
            // UV point not found in any triangle, use closest point
            return this.findClosestPoint(uv, positions, uvs, indices);
        }
        
        // Interpolate position within the triangle
        return this.interpolatePositionInTriangle(triangleData, positions, uvs, uv);
    }
    
    /**
     * Find triangle containing the given UV point
     */
    private static findTriangleContainingUV(
        uv: Vector2, 
        uvs: THREE.BufferAttribute, 
        indices: THREE.BufferAttribute | null
    ): { indices: [number, number, number] } | null {
        const triangleCount = indices ? indices.count / 3 : uvs.count / 3;
        
        for (let i = 0; i < triangleCount; i++) {
            const i1 = indices ? indices.getX(i * 3) : i * 3;
            const i2 = indices ? indices.getX(i * 3 + 1) : i * 3 + 1;
            const i3 = indices ? indices.getX(i * 3 + 2) : i * 3 + 2;
            
            // Get triangle UV coordinates
            const uv1 = new Vector2(uvs.getX(i1), uvs.getY(i1));
            const uv2 = new Vector2(uvs.getX(i2), uvs.getY(i2));
            const uv3 = new Vector2(uvs.getX(i3), uvs.getY(i3));
            
            // Check if point is inside triangle
            if (this.isPointInTriangle(uv, uv1, uv2, uv3)) {
                return { indices: [i1, i2, i3] };
            }
        }
        
        return null;
    }
    
    /**
     * Check if a point is inside a triangle using barycentric coordinates
     */
    private static isPointInTriangle(p: Vector2, a: Vector2, b: Vector2, c: Vector2): boolean {
        // Use component-wise operations instead of vector methods
        const v0x = c.x - a.x;
        const v0y = c.y - a.y;
        const v1x = b.x - a.x;
        const v1y = b.y - a.y;
        const v2x = p.x - a.x;
        const v2y = p.y - a.y;
        
        const dot00 = v0x * v0x + v0y * v0y;
        const dot01 = v0x * v1x + v0y * v1y;
        const dot02 = v0x * v2x + v0y * v2y;
        const dot11 = v1x * v1x + v1y * v1y;
        const dot12 = v1x * v2x + v1y * v2y;
        
        const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
        const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
        const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
        
        return (u >= 0) && (v >= 0) && (u + v <= 1);
    }
    
    /**
     * Interpolate 3D position within a triangle using barycentric coordinates
     */
    private static interpolatePositionInTriangle(
        triangleData: { indices: [number, number, number] },
        positions: THREE.BufferAttribute,
        uvs: THREE.BufferAttribute,
        targetUV: Vector2
    ): Vector3 {
        const [i1, i2, i3] = triangleData.indices;
        
        // Get triangle vertices
        const v1 = new Vector3(positions.getX(i1), positions.getY(i1), positions.getZ(i1));
        const v2 = new Vector3(positions.getX(i2), positions.getY(i2), positions.getZ(i2));
        const v3 = new Vector3(positions.getX(i3), positions.getY(i3), positions.getZ(i3));
        
        // Get triangle UV coordinates
        const uv1 = new Vector2(uvs.getX(i1), uvs.getY(i1));
        const uv2 = new Vector2(uvs.getX(i2), uvs.getY(i2));
        const uv3 = new Vector2(uvs.getX(i3), uvs.getY(i3));
        
        // Calculate barycentric coordinates
        const barycentric = this.getBarycentricCoordinates(targetUV, uv1, uv2, uv3);
        
        // Interpolate 3D position
        const result = v1.multiply(barycentric.x)
            .add(v2.multiply(barycentric.y))
            .add(v3.multiply(barycentric.z));
            
        return result;
    }
    
    /**
     * Calculate barycentric coordinates for a point in triangle
     */
    private static getBarycentricCoordinates(p: Vector2, a: Vector2, b: Vector2, c: Vector2): Vector3 {
        // Use component-wise operations
        const v0x = c.x - a.x;
        const v0y = c.y - a.y;
        const v1x = b.x - a.x;
        const v1y = b.y - a.y;
        const v2x = p.x - a.x;
        const v2y = p.y - a.y;
        
        const dot00 = v0x * v0x + v0y * v0y;
        const dot01 = v0x * v1x + v0y * v1y;
        const dot02 = v0x * v2x + v0y * v2y;
        const dot11 = v1x * v1x + v1y * v1y;
        const dot12 = v1x * v2x + v1y * v2y;
        
        const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
        const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
        const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
        const w = 1 - u - v;
        
        return new Vector3(w, v, u); // Return as (w, v, u) for vertex order
    }
    
    /**
     * Find closest point when UV is outside all triangles
     */
    private static findClosestPoint(
        uv: Vector2,
        positions: THREE.BufferAttribute,
        uvs: THREE.BufferAttribute,
        indices: THREE.BufferAttribute | null
    ): Vector3 {
        let closestDistance = Infinity;
        let closestPosition = new Vector3(0, 0, 0);
        
        const vertexCount = uvs.count;
        
        for (let i = 0; i < vertexCount; i++) {
            const vertexUV = new Vector2(uvs.getX(i), uvs.getY(i));
            // Calculate distance manually
            const dx = uv.x - vertexUV.x;
            const dy = uv.y - vertexUV.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < closestDistance) {
                closestDistance = distance;
                closestPosition = new Vector3(
                    positions.getX(i),
                    positions.getY(i),
                    positions.getZ(i)
                );
            }
        }
        
        return closestPosition;
    }
} 